import { useState, useCallback, useRef } from "react";

type Position = { readonly x: number; readonly y: number };
type Size = { readonly width: number; readonly height: number };

type DraggableOverlayResult = {
  readonly position: Position;
  readonly size: Size;
  readonly onDragPointerDown: (e: React.PointerEvent) => void;
  readonly onResizePointerDown: (e: React.PointerEvent) => void;
  readonly setPosition: (pos: Position) => void;
  readonly setSize: (size: Size) => void;
};

const MIN_WIDTH = 250;
const MIN_HEIGHT = 200;
const MAX_WIDTH = 800;
const MAX_HEIGHT = 900;

export const useDraggableOverlay = (
  initialPosition: Position,
  initialSize: Size,
): DraggableOverlayResult => {
  const [position, setPosition] = useState<Position>(initialPosition);
  const [size, setSize] = useState<Size>(initialSize);
  const dragOffset = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const onDragPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const target = e.currentTarget as HTMLElement;
      target.setPointerCapture(e.pointerId);

      dragOffset.current = {
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      };

      const onPointerMove = (ev: PointerEvent) => {
        setPosition({
          x: ev.clientX - dragOffset.current.x,
          y: ev.clientY - dragOffset.current.y,
        });
      };

      const onPointerUp = () => {
        target.releasePointerCapture(e.pointerId);
        target.removeEventListener("pointermove", onPointerMove);
        target.removeEventListener("pointerup", onPointerUp);
      };

      target.addEventListener("pointermove", onPointerMove);
      target.addEventListener("pointerup", onPointerUp);
    },
    [position.x, position.y],
  );

  const onResizePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const target = e.currentTarget as HTMLElement;
      target.setPointerCapture(e.pointerId);

      const startX = e.clientX;
      const startY = e.clientY;
      const startWidth = size.width;
      const startHeight = size.height;

      const onPointerMove = (ev: PointerEvent) => {
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        setSize({
          width: Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth + dx)),
          height: Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, startHeight + dy)),
        });
      };

      const onPointerUp = () => {
        target.releasePointerCapture(e.pointerId);
        target.removeEventListener("pointermove", onPointerMove);
        target.removeEventListener("pointerup", onPointerUp);
      };

      target.addEventListener("pointermove", onPointerMove);
      target.addEventListener("pointerup", onPointerUp);
    },
    [size.width, size.height],
  );

  return {
    position,
    size,
    onDragPointerDown,
    onResizePointerDown,
    setPosition,
    setSize,
  };
};
